import {Injectable} from '@angular/core';
import {HttpService} from '@app/core/services/http.service';
import {AuthService} from '@app/core/services/auth.service';
import {Department, IPunchArea} from '@app/core/models/department.model';
import {User} from '@app/core/models/user.model';
import {Group} from '@app/core/models/group.model';
import {RDate} from '@app/core/models/rDate.model';
import {BaseService} from '@app/core/services/base.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {Observable} from 'rxjs';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {RoundingRule} from '@app/core/models/roundingRule.model';
import {map} from 'rxjs/operators';

export interface IPatchPayload {
	department_name?: string;
	punch_clock_enabled?: boolean;
	payment_period?: number;
	can_user_punch?: boolean;
	punch_user_enabled?: boolean;
	payed_pause?: boolean;
	punch_password?: string;
	department_ip?: string;
	CVR?: number;
	operational_start_time?: string;
	payment_number?: string | null;
	address?: string;
	city?: string;
	postal_code?: string;
	email?: string;
	phone_ext?: string;
	phone_number?: number;
	punch_areas?: IPunchArea[] | string;
	finished_payment_period?: boolean;
	permanently_employed_supplements_enabled?: boolean;
	rounding_rules?: RoundingRule[];
	shift_change_notification_interval?: number;
}

@Injectable()
export class DepartmentService extends BaseService {
	constructor(
		public authService: AuthService,
		public interService: InternationalizationService,
		public httpService: HttpService,
		public store: Store<IAppState>
	) {
		super(authService, interService, httpService, store);
	}

	public fresh(params?): Department {
		if (!params) {
			return new Department(this.tenantTZ, this.interService);
		}

		return new Department(this.tenantTZ, this.interService).deserialize(params);
	}

	public freshRoundingRule(params?): RoundingRule {
		if (!params) {
			return new RoundingRule(this.tenantTZ, this.interService);
		}

		return new RoundingRule(this.tenantTZ, this.interService).deserialize(params);
	}

	public get(id: number): Observable<Department> {
		return this.httpService.getJson<Department>('departments/' + id).pipe(map(res => this.fresh(res)));
	}

	public patch(id: number, payload: IPatchPayload): Observable<Department> {
		return this.httpService.patchJson<{ department: Department }>('departments/' + id, payload).pipe(map(res => this.fresh(res.department)));
	}

	public getDepartments(): Observable<Department[]> {
		return this.httpService.getJson<Department[]>('departments').pipe(
			map((res: Department[]) => res.map(department => new Department(this.tenantTZ, this.interService).deserialize(department)))
		);
	}

	public getGroups(departmentId: number): Observable<Group[]> {
		return this.httpService.getJson<Group[]>('departments/' + departmentId + '/groups', {include: 'users'})
			.pipe(
				map((res: Group[]) => res.map(group => new Group(this.tenantTZ, this.interService).deserialize(group)))
			);
	}
}
